"use client";

import type { JSX } from "react";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import type { Article } from "@/lib/directus";
import type { CategoryCounts } from "@/lib/directus";
import { trackCategoryFilter } from "@/lib/analytics";

interface CategoryFilterProps {
  /**
   * The current article list. Used as the input for on-page client-side
   * filtering (when `onFilter` is provided). Callers that only need counts
   * should prefer `categoryCounts` and leave this empty to avoid shipping
   * large article payloads to the client.
   */
  articles?: Article[];
  /**
   * Pre-computed category counts for the pill badges. When provided, the
   * filter never needs the full article list to render counts.
   */
  categoryCounts?: CategoryCounts;
  onFilter?: (filtered: Article[]) => void;
  initialCategory?: string | null;
  showLabel?: boolean;
  className?: string;
  useNavigation?: boolean; // If true, navigate instead of filtering
  basePath?: string; // Base path for navigation (e.g., "/blog")
}

type Category = "All" | "Training" | "Nutrition" | "Lifestyle";

// Category-specific color configurations for pills
const FILTER_COLORS: Record<
  Category,
  {
    inactive: string;
    active: string;
    hover: string;
    focus: string;
  }
> = {
  All: {
    inactive: "bg-gray-100 text-gray-700",
    active: "bg-gray-900 text-white shadow-lg shadow-gray-900/20",
    hover: "hover:bg-gray-200 hover:shadow-md hover:-translate-y-0.5",
    focus: "focus-visible:ring-gray-600",
  },
  Training: {
    inactive: "bg-gray-100 text-gray-700",
    active: "bg-blue-600 text-white shadow-lg shadow-blue-600/30",
    hover: "hover:bg-blue-50 hover:text-blue-700 hover:shadow-md hover:-translate-y-0.5",
    focus: "focus-visible:ring-blue-600",
  },
  Nutrition: {
    inactive: "bg-gray-100 text-gray-700",
    active: "bg-green-600 text-white shadow-lg shadow-green-600/30",
    hover: "hover:bg-green-50 hover:text-green-700 hover:shadow-md hover:-translate-y-0.5",
    focus: "focus-visible:ring-green-600",
  },
  Lifestyle: {
    inactive: "bg-gray-100 text-gray-700",
    active: "bg-purple-600 text-white shadow-lg shadow-purple-600/30",
    hover: "hover:bg-purple-50 hover:text-purple-700 hover:shadow-md hover:-translate-y-0.5",
    focus: "focus-visible:ring-purple-600",
  },
};

export function CategoryFilter({
  articles,
  categoryCounts: categoryCountsProp,
  onFilter,
  initialCategory = null,
  showLabel = true,
  className = "",
  useNavigation = false,
  basePath = "/blog",
}: CategoryFilterProps): JSX.Element {
  const articleList = articles ?? [];
  const router = useRouter();
  const pathname = usePathname();
  const [activeCategory, setActiveCategory] = useState<string | null>(
    initialCategory
  );
  const [animatingCategory, setAnimatingCategory] = useState<Category | null>(
    null
  );

  // Determine current category from pathname
  useEffect(() => {
    if (pathname === "/training") {
      setActiveCategory("Training");
    } else if (pathname === "/nutrition") {
      setActiveCategory("Nutrition");
    } else if (pathname === "/lifestyle") {
      setActiveCategory("Lifestyle");
    } else if (pathname === "/blog" || pathname === basePath) {
      setActiveCategory(null); // "All"
    }
  }, [pathname, basePath]);

  const categoryCounts = useMemo(() => {
    if (categoryCountsProp) return categoryCountsProp;
    return {
      All: articleList.length,
      Training: articleList.filter((a) => a.category === "Training").length,
      Nutrition: articleList.filter((a) => a.category === "Nutrition").length,
      Lifestyle: articleList.filter((a) => a.category === "Lifestyle").length,
    };
  }, [articleList, categoryCountsProp]);

  const handleFilter = useCallback(
    (category: Category) => {
      trackCategoryFilter(category);
      setAnimatingCategory(category);
      setTimeout(() => setAnimatingCategory(null), 300);

      if (useNavigation) {
        if (category === "All") {
          router.push(basePath);
        } else {
          router.push(`/${category.toLowerCase()}`);
        }
      } else {
        if (onFilter) {
          if (category === "All") {
            onFilter(articleList);
            setActiveCategory(null);
          } else {
            const filtered = articleList.filter(
              (article) => article.category === category
            );
            onFilter(filtered);
            setActiveCategory(category);
          }
        }
      }
    },
    [articleList, onFilter, useNavigation, router, basePath]
  );

  useEffect(() => {
    if (!useNavigation && initialCategory && onFilter) {
      const category = initialCategory as Category;
      if (category === "All") {
        onFilter(articleList);
      } else {
        const filtered = articleList.filter(
          (article) => article.category === category
        );
        onFilter(filtered);
      }
    }
  }, []);

  const categories: Category[] = ["All", "Training", "Nutrition", "Lifestyle"];

  return (
    <div className={`mt-6 mb-8 ${className}`}>
      {showLabel && (
        <p className="text-sm font-medium text-gray-700 mb-4">
          Filter by category
        </p>
      )}
      <div
        role="group"
        aria-label="Filter articles by category"
        className="flex flex-wrap items-center gap-1.5 sm:gap-2"
      >
        {categories.map((category) => {
          const isActive =
            category === "All"
              ? activeCategory === null
              : activeCategory === category;
          const colors = FILTER_COLORS[category];
          const count = categoryCounts[category];
          const isAnimating = animatingCategory === category;

          return (
            <button
              key={category}
              type="button"
              role="button"
              aria-pressed={isActive}
              aria-label={`Filter by ${category} - ${count} ${count === 1 ? "article" : "articles"}`}
              onClick={() => handleFilter(category)}
              className={`
                group
                inline-flex items-center gap-1.5
                rounded-full
                px-3 py-1.5 sm:px-4 sm:py-2
                text-xs sm:text-sm font-medium
                transition-all duration-300 ease-out
                cursor-pointer
                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-offset-2
                motion-safe:transform
                ${isActive
                  ? `${colors.active} scale-105`
                  : `${colors.inactive} ${colors.hover} active:scale-95`
                }
                ${colors.focus}
                ${isAnimating ? 'motion-safe:animate-[pop_0.3s_ease-out]' : ''}
              `}
            >
              {/* Category label */}
              <span>{category}</span>

              {/* Count badge */}
              <span
                className={`
                  inline-flex items-center justify-center
                  min-w-[1.25rem] sm:min-w-[1.5rem] h-4 sm:h-5
                  px-1
                  rounded-full
                  text-[0.625rem] sm:text-xs font-semibold
                  transition-all duration-300
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-200 text-gray-700 group-hover:bg-gray-300'
                  }
                `}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
